// src/pages/club/settings/subscription/steps/ClubStep4.jsx
import "./ClubStep4.scss";

import { useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import ClubPlanAPI from "../../api/ClubPlanAPI";
import HallAppToast from "../../../../../commons/HallAppToast";

const clubPlanAPI = new ClubPlanAPI();

function formatBRL(v) {
  const n = Number(v || 0);
  try {
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function frequencyLabel(v) {
  switch (v) {
    case "MONTHLY":
      return "Mensal";
    case "QUARTERLY":
      return "Trimestral";
    case "YEARLY":
      return "Anual";
    default:
      return v || "-";
  }
}

function billingRuleLabel(v, fixedDay) {
  if (v === "FIXED_DAY_OF_MONTH") return `Dia fixo do mês (dia ${fixedDay || "—"})`;
  if (v === "ON_SUBSCRIPTION_DAY") return "No dia da adesão";
  return v || "-";
}

function benefitTypeLabel(type) {
  switch (type) {
    case "CREDIT":
      return "Crédito";
    case "DISCOUNT_PERCENT":
      return "Desconto (%)";
    case "DISCOUNT_AMOUNT":
      return "Desconto (R$)";
    case "INCLUDED_SERVICE":
      return "Serviço incluso";
    default:
      return type || "-";
  }
}

function benefitRightPill(b) {
  if (!b) return "";
  if (b.type === "CREDIT" || b.type === "DISCOUNT_AMOUNT") return formatBRL(b.value);
  if (b.type === "DISCOUNT_PERCENT") return `${Number(b.value || 0)}%`;
  if (b.type === "INCLUDED_SERVICE") return `${b.qty || 1}x`;
  return "";
}

function SummaryRow({ label, value }) {
  return (
    <div className="club-step4__row">
      <div className="club-step4__row-label text-muted">{label}</div>
      <div className="club-step4__row-value">{value || "—"}</div>
    </div>
  );
}

function Chip({ children }) {
  return <span className="club-step4__chip">{children}</span>;
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function redemptionFrequencyLabel(f) {
  switch (f) {
    case "WEEKLY":
      return "Semanal";
    case "FORTNIGHTLY":
      return "Quinzenal";
    case "MONTHLY":
      return "Mensal";
    case "CYCLE":
      return "Por ciclo do plano";
    default:
      return f || "—";
  }
}

function weekLabel(k) {
  switch (k) {
    case "SUNDAY":
      return "Dom";
    case "MONDAY":
      return "Seg";
    case "TUESDAY":
      return "Ter";
    case "WEDNESDAY":
      return "Qua";
    case "THURSDAY":
      return "Qui";
    case "FRIDAY":
      return "Sex";
    case "SATURDAY":
      return "Sáb";
    default:
      return k;
  }
}

function ClubStep4({ data, toBack, closeModal, onSuccess, clubPlanId }) {
  const [isLoading, setIsLoading] = useState(false);

  const step1 = data || {};
  const benefits = Array.isArray(data?.benefits) ? data.benefits : [];
  const rules = data?.rules || {};
  const redemption = rules?.redemption || {};

  const planName = step1?.name || "";
  const amount = step1?.amount;
  const currency = step1?.currency || "BRL";
  const frequency = step1?.frequency;
  const billingRule = step1?.billingRule;
  const fixedDayOfMonth = step1?.fixedDayOfMonth;

  const payload = useMemo(() => {
    const redemptionEnabled = !!redemption?.enabled;
    const redemptionQty = redemptionEnabled ? toNumberOrNull(redemption?.qty) ?? 1 : 1;
    const redemptionFrequency = redemptionEnabled ? redemption?.frequency || null : null;

    const redemptionWeeks =
      redemptionEnabled && (redemptionFrequency === "WEEKLY" || redemptionFrequency === "FORTNIGHTLY")
        ? Array.isArray(redemption?.weeks)
          ? redemption.weeks
          : []
        : [];

    return {
      // Step 1
      name: step1?.name?.trim(),
      shortDescription: step1?.shortDescription?.trim() || null,
      fullDescription: step1?.fullDescription?.trim() || null,

      amount: Number(step1?.amount || 0),
      currency: step1?.currency || "BRL",

      frequency: step1?.frequency || "MONTHLY",
      billingRule: step1?.billingRule || "ON_SUBSCRIPTION_DAY",
      fixedDayOfMonth:
        step1?.billingRule === "FIXED_DAY_OF_MONTH"
          ? toNumberOrNull(step1?.fixedDayOfMonth)
          : null,

      limitSubscribers: toNumberOrNull(step1?.limitSubscribers),
      allowMultipleSubscriptionsPerCustomer: !!step1?.allowMultipleSubscriptionsPerCustomer,

      visibleOnSite: !!step1?.visibleOnSite,
      active: !!step1?.active,
      displayOrder: toNumberOrNull(step1?.displayOrder),

      // Step 2
      benefits: benefits.map((b) => ({
        type: b.type,
        title: (b.title || "").trim(),
        description: (b.description || "").trim() || null,
        value: b.value != null ? Number(b.value) : null,
        serviceId: b.serviceId != null ? Number(b.serviceId) : null,
        qty: b.qty != null ? Number(b.qty) : null,
      })),

      // Step 3
      rules: {
        autoRenew: rules?.autoRenew !== false,

        allowCarryOver: !!rules.allowCarryOver,
        carryOverLimitCycles: rules.allowCarryOver ? toNumberOrNull(rules.carryOverLimitCycles) : null,

        creditExpiresInDays: toNumberOrNull(rules.creditExpiresInDays),
        includedServiceExpiresInDays: toNumberOrNull(rules.includedServiceExpiresInDays),

        // ✅ NOVO (vem do Step3)
        redemption: {
          enabled: redemptionEnabled,
          qty: redemptionQty,
          frequency: redemptionFrequency,
          weeks: redemptionWeeks,
        },

        // ✅ compat opcional: se "por ciclo", preenche o legado
        maxRedemptionsPerCycle:
          redemptionEnabled && redemptionFrequency === "CYCLE" ? redemptionQty : null,

        termsText: (rules.termsText || "").trim() || null,
      },
    };
  }, [step1, benefits, rules, redemption]);

  const createClub = async () => {
    try {
      if (!payload?.name) {
        HallAppToast.error("Informe o nome do clube.");
        return;
      }
      if (!payload?.amount || payload.amount <= 0) {
        HallAppToast.error("Informe um valor válido.");
        return;
      }

      setIsLoading(true);

      if (clubPlanId) {
        await clubPlanAPI.update(clubPlanId, payload);
      } else {
        await clubPlanAPI.create(payload);
      }

      HallAppToast.successWithMessage("Clube salvo com sucesso!");
      onSuccess?.();
      closeModal?.();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  const redemptionText = (() => {
    if (!redemption?.enabled) return "Não definido";

    const qty = redemption?.qty || 1;
    const freq = redemption?.frequency;

    if (!freq) return "Não definido";

    if (freq === "WEEKLY") {
      const ds = Array.isArray(redemption?.weeks) ? redemption.weeks : [];
      const days = ds.length ? ds.map(weekLabel).join(", ") : "—";
      return `${qty}x • ${redemptionFrequencyLabel(freq)} • Dias: ${days}`;
    }

    if (freq === "FORTNIGHTLY") {
      const ds = Array.isArray(redemption?.weeks) ? redemption.weeks : [];
      const day = ds[0] ? weekLabel(ds[0]) : "Sem dia preferencial";
      return `${qty}x • ${redemptionFrequencyLabel(freq)} • ${day}`;
    }

    return `${qty}x • ${redemptionFrequencyLabel(freq)}`;
  })();

  return (
    <div className="club-step4" style={{ paddingBottom: 120 }}>
      <div className="mb-3">
        <div className="club-step4__title">Revisão e confirmação</div>
        <div className="club-step4__subtitle text-muted">Confira tudo antes de salvar.</div>
      </div>

      {/* Plano */}
      <div className="club-step4__card">
        <div className="club-step4__card-title">Plano</div>
        <div className="club-step4__card-hint text-muted">Nome, preço e recorrência.</div>

        <SummaryRow label="Nome" value={planName} />
        <SummaryRow label="Valor" value={currency === "BRL" ? formatBRL(amount) : String(amount)} />
        <SummaryRow label="Recorrência" value={frequencyLabel(frequency)} />
        <SummaryRow label="Regra de cobrança" value={billingRuleLabel(billingRule, fixedDayOfMonth)} />

        {step1?.shortDescription ? (
          <div className="club-step4__block">
            <div className="club-step4__block-label text-muted">Descrição curta</div>
            <div className="club-step4__block-text">{step1.shortDescription}</div>
          </div>
        ) : null}

        {step1?.fullDescription ? (
          <div className="club-step4__block">
            <div className="club-step4__block-label text-muted">Descrição completa</div>
            <div className="club-step4__block-text">{step1.fullDescription}</div>
          </div>
        ) : null}

        <div className="club-step4__chips">
          {step1?.active ? <Chip>Ativo</Chip> : <Chip>Inativo</Chip>}
          {step1?.visibleOnSite ? <Chip>Visível no site</Chip> : <Chip>Oculto no site</Chip>}
          {step1?.limitSubscribers ? <Chip>Limite: {step1.limitSubscribers}</Chip> : null}
        </div>
      </div>

      {/* Benefícios */}
      <div className="club-step4__card">
        <div className="club-step4__card-title">Benefícios</div>
        <div className="club-step4__card-hint text-muted">{benefits.length} benefício(s) adicionados.</div>

        {benefits.length === 0 ? (
          <div className="club-step4__empty">Nenhum benefício informado.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {benefits.map((b, idx) => (
              <div key={`${b.type}-${idx}`} className="club-step4__item">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div style={{ minWidth: 0 }}>
                    <div className="club-step4__item-title">{b.title}</div>
                    <div className="club-step4__item-meta text-muted">
                      {benefitTypeLabel(b.type)}
                      {b.type === "INCLUDED_SERVICE" && b.serviceName ? ` • ${b.serviceName}` : ""}
                    </div>
                    {b.description ? (
                      <div className="club-step4__item-desc text-muted">{b.description}</div>
                    ) : null}
                  </div>

                  {benefitRightPill(b) ? (
                    <span className="club-step4__pill">{benefitRightPill(b)}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Regras */}
      <div className="club-step4__card">
        <div className="club-step4__card-title">Regras</div>
        <div className="club-step4__card-hint text-muted">Acúmulo, expiração e frequência.</div>

        <SummaryRow
          label="Acumular benefícios"
          value={rules.allowCarryOver ? `Sim (até ${rules.carryOverLimitCycles || "—"} ciclo(s))` : "Não"}
        />

        <SummaryRow
          label="Crédito expira"
          value={rules.creditExpiresInDays != null ? `${rules.creditExpiresInDays} dia(s)` : "Não definido"}
        />

        <SummaryRow
          label="Serviço incluso expira"
          value={
            rules.includedServiceExpiresInDays != null
              ? `${rules.includedServiceExpiresInDays} dia(s)`
              : "Não definido"
          }
        />

        {/* ✅ AJUSTADO */}
        <SummaryRow label="Frequência de uso" value={redemptionText} />

        {rules.termsText ? (
          <div className="club-step4__block">
            <div className="club-step4__block-label text-muted">Texto para o cliente</div>
            <div className="club-step4__block-text">{rules.termsText}</div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="club-step4__footer">
        <AppContainer>
          {isLoading ? (
            <AppLoading />
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                <AppButton name="Salvar" width="100%" onClick={createClub} />
              </div>
              <AppLinkButton name="Voltar" fullWidth={true} onClick={toBack} />
            </>
          )}
        </AppContainer>
      </div>
    </div>
  );
}

export default ClubStep4;
